import { ElementRef, Inject, Injectable, isDevMode } from '@angular/core';
import { NavigationSectionConfigurationInterface } from '../directives/navigation-section.directive';
import { NavigationFieldConfigurationInterface } from '../directives/navigation-field.directive';
import { NavigationContainerConfigurationInterface } from '../directives/navigation-container.directive';
import { NAVIGATION_STRATEGY } from './navigation-strategy.factory';
import { NavigationStrategyInterface } from '../interfaces/navigation-strategy.interface';
import { Subject } from 'rxjs';

export enum ClosestSectionPosition {
    Next = 'next',
    Previous = 'previous'
}

export enum ClosestFieldPosition {
    Next = 'next',
    Previous = 'previous'
}

export enum NavigationElementTypes {
    Container,
    Section,
    Field
}

export enum NavigationEventTypes {
    Registration,
    Unregistration,
}

export interface NavigationEvent {
    type: NavigationEventTypes;
    elementId: string;
    elementType: NavigationElementTypes;
}

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    static UP_KEY: number[] = [38];
    static DOWN_KEY: number[] = [40];
    static RIGHT_KEY: number[] = [39];
    static LEFT_KEY: number[] = [37];
    static ENTER_KEY: number[] = [13];

    onEvent = new Subject<NavigationEvent>();

    /** Flag to enable or disable debug navigation mode */
    debugNavigation = false;

    #activeContainer: string | null = null;
    #activeSection: string | null = null;
    #activeField: string | null = null;
    #lastToolbarSectionByContainer = new Map<string, string>();
    #lastFieldsSectionByContainer = new Map<string, string>();
    #lastNavigationAction: string | null = null;

    #registeredContainers: { [key: string]: NavigationContainerConfigurationInterface; } = {};
    #registeredSections: { [key: string]: NavigationSectionConfigurationInterface; } = {};
    #registeredFields: { [key: string]: NavigationFieldConfigurationInterface; } = {};

    #containerByElement = new Map<HTMLElement, NavigationContainerConfigurationInterface>();
    #sectionByElement = new Map<HTMLElement, NavigationSectionConfigurationInterface>();

    #previousClosestSection: NavigationSectionConfigurationInterface | null = null;
    #nextClosestSection: NavigationSectionConfigurationInterface | null = null;

    #previousClosestField: NavigationFieldConfigurationInterface | null = null;
    #nextClosestField: NavigationFieldConfigurationInterface | null = null;

    constructor(
        @Inject(NAVIGATION_STRATEGY) private navigationStrategy: NavigationStrategyInterface,
    ) {
        if (!isDevMode()) {
            this.debugNavigation = false;
        }
    }

    get lastToolbarSection(): NavigationSectionConfigurationInterface | null {
        if (!this.activeContainer) {
            return null;
        }

        const sectionId = this.#lastToolbarSectionByContainer.get(this.activeContainer.containerId);

        return sectionId ? this.#registeredSections[sectionId] : null;
    }

    get lastFieldsSection(): NavigationSectionConfigurationInterface | null {
        if (!this.activeContainer) {
            return null;
        }

        const sectionId = this.#lastFieldsSectionByContainer.get(this.activeContainer.containerId);

        return sectionId ? this.#registeredSections[sectionId] : null;
    }

    get activeContainer(): NavigationContainerConfigurationInterface | null {
        if (this.#activeContainer) {
            return this.#registeredContainers[this.#activeContainer];
        }

        return null;
    }

    get activeSection(): NavigationSectionConfigurationInterface | null {
        if (this.#activeSection) {
            return this.#registeredSections[this.#activeSection];
        }

        return null;
    }

    get toolbarSections(): NavigationSectionConfigurationInterface[] {
        return Object.values(this.#registeredSections)
            .filter((section) => section.isToolbar && section.containerId === this.activeContainer?.containerId)
            .sort((a, b) => a.navigationSectionOrder - b.navigationSectionOrder);
    }

    get fieldsSections(): NavigationSectionConfigurationInterface[] {
        return Object.values(this.#registeredSections)
            .filter((section) => !section.isToolbar && section.containerId === this.activeContainer?.containerId)
            .sort((a, b) => a.navigationSectionOrder - b.navigationSectionOrder);
    }

    get activeField(): NavigationFieldConfigurationInterface | null {
        if (this.#activeField) {
            return this.#registeredFields[this.#activeField];
        }

        return null;
    }

    get fieldsFromActiveSection(): NavigationFieldConfigurationInterface[] {
        if (this.activeSection) {
            return Object.values(this.#registeredFields)
                .filter((field) => field.sectionId === this.activeSection.sectionId)
                .sort((a, b) => a.navigationFieldOrder - b.navigationFieldOrder);
        }

        return [];
    }

    get fieldsFromActiveContainer(): NavigationFieldConfigurationInterface[] {
        if (this.activeContainer) {
            return Object.values(this.#registeredFields)
                .filter((field) =>
                    this.getSectionsFromContainer(this.activeContainer)
                        .some((section) => section.sectionId === field.sectionId)
                )
                .sort((a, b) => a.navigationFieldOrder - b.navigationFieldOrder);
        }

        return [];
    }

    get sectionsFromActiveContainer(): NavigationSectionConfigurationInterface[] {
        if (this.activeContainer) {
            return Object.values(this.#registeredSections)
                .filter((section) =>
                    section.containerId === this.activeContainer.containerId &&
                    this.getFieldsFromSection(section).length > 0
                )
                .sort((a, b) => a.navigationSectionOrder - b.navigationSectionOrder);
        }

        return [];
    }

    get previousClosestSection(): NavigationSectionConfigurationInterface | null {
        return this.#previousClosestSection;
    }

    get nextClosestSection(): NavigationSectionConfigurationInterface | null {
        return this.#nextClosestSection;
    }

    get previousClosestField(): NavigationFieldConfigurationInterface | null {
        return this.#previousClosestField;
    }

    get nextClosestField(): NavigationFieldConfigurationInterface | null {
        return this.#nextClosestField;
    }

    registerField(fieldConfiguration: NavigationFieldConfigurationInterface) {
        // console.log('registerField', fieldConfiguration.fieldId, fieldConfiguration.element.nativeElement);

        const matchedSection = this.findClosestSectionFromElement(fieldConfiguration.element);

        if (matchedSection) {
            fieldConfiguration.sectionId = matchedSection.sectionId;

            // Se non viene passato un ordine, incremento l'ordine di 1 rispetto all'ultimo registrato della stesso section
            if (fieldConfiguration.navigationFieldOrder == null) {
                const maxOrder = this.getFieldsFromSection(matchedSection).reduce(
                    (max, field) => Math.max(max, field.navigationFieldOrder),
                    -1
                );

                fieldConfiguration.navigationFieldOrder = maxOrder + 1;
            }

            this.#registeredFields[fieldConfiguration.fieldId] = fieldConfiguration;

            this.onEvent.next({
                elementId: fieldConfiguration.fieldId,
                type: NavigationEventTypes.Registration,
                elementType: NavigationElementTypes.Field,
            } as NavigationEvent);

            return;
        }

        console.error(`NavigationService Field ${fieldConfiguration.fieldId} not found in any section`, fieldConfiguration.element);
    }

    private findClosestSectionFromElement(elementRef: ElementRef): NavigationSectionConfigurationInterface | null {
        let current: HTMLElement | null = elementRef?.nativeElement ?? null;

        while (current) {
            const matched = this.#sectionByElement.get(current);

            if (matched) {
                return matched;
            }

            current = current.parentElement;
        }

        return null;
    }

    unregisterField(fieldConfiguration: NavigationFieldConfigurationInterface) {
        // console.log('unregisterField', fieldConfiguration.fieldId, fieldConfiguration.element.nativeElement);

        if (this.#registeredFields[fieldConfiguration.fieldId]) {
            delete this.#registeredFields[fieldConfiguration.fieldId];

            this.onEvent.next({
                elementId: fieldConfiguration.fieldId,
                type: NavigationEventTypes.Unregistration,
                elementType: NavigationElementTypes.Field,
            } as NavigationEvent);
        }
    }

    registerSection(sectionConfiguration: NavigationSectionConfigurationInterface) {
        // console.log('registerSection', sectionConfiguration.sectionId, sectionConfiguration.element.nativeElement);

        const matchedContainer = this.findClosestContainerFromElement(sectionConfiguration.element);

        if (matchedContainer) {
            sectionConfiguration.containerId = matchedContainer.containerId;

            // Se non viene passato un ordine, incremento l'ordine di 1 rispetto all'ultimo registrato della stesso container
            if (sectionConfiguration.navigationSectionOrder == null) {
                const maxOrder = this.getSectionsFromContainer(matchedContainer).reduce(
                    (max, section) => Math.max(max, section.navigationSectionOrder),
                    -1
                );

                sectionConfiguration.navigationSectionOrder = maxOrder + 1;
            }

            this.#registeredSections[sectionConfiguration.sectionId] = sectionConfiguration;
            this.#sectionByElement.set(sectionConfiguration.element.nativeElement, sectionConfiguration);

            this.onEvent.next({
                elementId: sectionConfiguration.sectionId,
                type: NavigationEventTypes.Registration,
                elementType: NavigationElementTypes.Section,
            } as NavigationEvent);

            this.validateRegistryConsistency();

            return;
        }

        console.error(`NavigationService Section ${sectionConfiguration.sectionId} not found in any container`, sectionConfiguration.element);
    }

    private findClosestContainerFromElement(elementRef: ElementRef): NavigationContainerConfigurationInterface | null {
        let current: HTMLElement | null = elementRef?.nativeElement ?? null;

        while (current) {
            const matched = this.#containerByElement.get(current);

            if (matched) {
                return matched;
            }

            current = current.parentElement;
        }

        return null;
    }

    unregisterSection(sectionConfiguration: NavigationSectionConfigurationInterface) {
        // console.log('unregisterSection', sectionConfiguration.sectionId, sectionConfiguration.element.nativeElement);

        if (this.#registeredSections[sectionConfiguration.sectionId]) {
            if (this.#lastToolbarSectionByContainer.get(sectionConfiguration.containerId) === sectionConfiguration.sectionId) {
                this.#lastToolbarSectionByContainer.delete(sectionConfiguration.containerId);
            }

            if (this.#lastFieldsSectionByContainer.get(sectionConfiguration.containerId) === sectionConfiguration.sectionId) {
                this.#lastFieldsSectionByContainer.delete(sectionConfiguration.containerId);
            }

            this.#sectionByElement.delete(sectionConfiguration.element?.nativeElement);
            delete this.#registeredSections[sectionConfiguration.sectionId];

            this.onEvent.next({
                elementId: sectionConfiguration.sectionId,
                type: NavigationEventTypes.Unregistration,
                elementType: NavigationElementTypes.Section,
            } as NavigationEvent);

            this.validateRegistryConsistency();
        }
    }

    registerContainer(containerConfiguration: NavigationContainerConfigurationInterface) {
        if (containerConfiguration) {
            this.#registeredContainers[containerConfiguration.containerId] = containerConfiguration;
            this.#containerByElement.set(containerConfiguration.element.nativeElement, containerConfiguration);

            this.onEvent.next({
                elementId: containerConfiguration.containerId,
                type: NavigationEventTypes.Registration,
                elementType: NavigationElementTypes.Container,
            } as NavigationEvent);

            this.validateRegistryConsistency();
        } else {
            console.error('NavigationService container name not set', containerConfiguration);
        }
    }

    unregisterContainer(containerConfiguration: NavigationContainerConfigurationInterface) {
        this.#containerByElement.delete(containerConfiguration.element?.nativeElement);
        delete this.#registeredContainers[containerConfiguration.containerId];
        this.#lastToolbarSectionByContainer.delete(containerConfiguration.containerId);
        this.#lastFieldsSectionByContainer.delete(containerConfiguration.containerId);

        this.onEvent.next({
            elementId: containerConfiguration.containerId,
            type: NavigationEventTypes.Unregistration,
            elementType: NavigationElementTypes.Container,
        } as NavigationEvent);

        this.validateRegistryConsistency();
    }

    setActiveContainer(containerId: string) {
        if (!this.#registeredContainers[containerId]) {
            console.error('NavigationService container not found', containerId);
            return;
        }

        if (this.activeContainer?.containerId !== containerId) {
            this.#activeContainer = containerId;
            this.activeContainer.onActive();
            this.logNavigationChange('container', containerId);
        }
    }

    setActiveSectionFromSubelement(element: ElementRef) {
        const matchedSection = this.findClosestSectionFromElement(element);

        if (matchedSection) {
            this.setActiveSection(matchedSection.sectionId);
        }
    }

    private validateRegistryConsistency() {
        const staleContainers = Array.from(this.#containerByElement.values())
            .filter((container) => !this.#registeredContainers[container.containerId]);
        const staleSections = Array.from(this.#sectionByElement.values())
            .filter((section) => !this.#registeredSections[section.sectionId]);
        const missingContainers = Object.values(this.#registeredContainers)
            .filter((container) => !this.#containerByElement.has(container.element.nativeElement));
        const missingSections = Object.values(this.#registeredSections)
            .filter((section) => !this.#sectionByElement.has(section.element.nativeElement));

        if (staleContainers.length || staleSections.length || missingContainers.length || missingSections.length) {
            console.warn('NavigationService registry mismatch', {
                staleContainers: staleContainers.length,
                staleSections: staleSections.length,
                missingContainers: missingContainers.length,
                missingSections: missingSections.length,
            });
        }
    }

    setActiveSectionFromSectionElement(element: ElementRef) {
        for (const id in this.#registeredSections) {
            if (Object.prototype.hasOwnProperty.call(this.#registeredSections, id)) {
                const sectionConfiguration: NavigationSectionConfigurationInterface = this.#registeredSections[id];
                if (sectionConfiguration.element.nativeElement === element.nativeElement) {
                    this.setActiveSection(id);
                    return;
                }
            }
        }
    }

    setActiveFieldFromFieldElement(element: ElementRef) {
        for (const id in this.#registeredFields) {
            if (Object.prototype.hasOwnProperty.call(this.#registeredFields, id)) {
                const fieldConfiguration: NavigationFieldConfigurationInterface = this.#registeredFields[id];
                if (fieldConfiguration.element.nativeElement === element.nativeElement) {
                    this.setActiveField(id);
                    return;
                }
            }
        }
    }

    setActiveSection(sectionId: string | null) {
        if (sectionId == null) {
            this.#activeSection = null;
            this.logNavigationChange('section', null);
            return;
        }

        if (this.#registeredSections[sectionId]) {
            if (this.activeSection?.sectionId !== sectionId) {
                const container = this.getContainerFromSectionId(sectionId);

                if (container) {
                    this.setActiveContainer(container.containerId);
                }

                this.#activeSection = sectionId;

                if (this.activeSection.isToolbar) {
                    this.setLastToolbarSection(this.activeSection);
                } else {
                    this.setLastFieldsSection(this.activeSection);
                }

                this.logNavigationChange('section', sectionId);
            }
        } else {
            console.error('NavigationService section not found', sectionId, this.#registeredSections);
        }
    }

    setActiveSectionAndHisFirstField(sectionId: string) {
        this.setActiveSection(sectionId);
        const section = this.getSectionFromSectionId(sectionId);
        if (section) {
            const element = this.getFieldsFromSection(section);
            if (element && element?.length > 0) {
                this.setActiveField(element[0].fieldId);
            }
        }
    }

    setLastToolbarSection(section: NavigationSectionConfigurationInterface) {
        if (section?.containerId) {
            this.#lastToolbarSectionByContainer.set(section.containerId, section.sectionId);
        }
    }

    setLastFieldsSection(section: NavigationSectionConfigurationInterface) {
        if (section?.containerId) {
            this.#lastFieldsSectionByContainer.set(section.containerId, section.sectionId);
        }
    }

    setClosestField({ field, position }: { field: NavigationFieldConfigurationInterface, position: ClosestFieldPosition; }) {
        if (position === ClosestFieldPosition.Next) {
            this.#nextClosestField = field;

            this.#previousClosestField = null;

        } else if (position === ClosestFieldPosition.Previous) {
            this.#previousClosestField = field;

            this.#nextClosestField = null;
        }
    }

    setClosestSection({ section, position }: { section: NavigationSectionConfigurationInterface, position: ClosestSectionPosition; }) {
        if (position === ClosestSectionPosition.Next) {
            this.#nextClosestSection = section;

            this.#previousClosestSection = null;

        } else if (position === ClosestSectionPosition.Previous) {
            this.#previousClosestSection = section;

            this.#nextClosestSection = null;
        }
    }

    handleKeyPress(event: KeyboardEvent) {
        this.navigationStrategy.handleKeyPress(event);
    }

    setActiveField(id: string | null, focusElement = true) {
        if (id == this.activeField?.fieldId) {
            return;
        }

        if (id == null) {
            this.#activeField = null;
            this.logNavigationChange('field', null);

            return;
        }

        if (this.#registeredFields[id]) {
            if (this.activeField?.fieldId !== id) {
                this.#activeField = id;

                const section = this.getSectionFromField(this.activeField);

                if (section) {
                    this.setActiveSection(section.sectionId);
                }

                if (focusElement) {
                    this.activeField.onFocus();
                }

                this.logNavigationChange('field', id);
            }
        } else {
            console.error('NavigationService field not found', id, this.#registeredFields);
        }
    }

    private logNavigationChange(type: 'container' | 'section' | 'field', id: string | null) {
        if (!this.debugNavigation || !isDevMode()) {
            return;
        }

        console.log('[NavigationService]', {
            type,
            id,
            action: this.#lastNavigationAction,
            activeContainer: this.#activeContainer,
            activeSection: this.#activeSection,
            activeField: this.#activeField,
            lastToolbarSection: this.#lastToolbarSectionByContainer.get(this.activeContainer?.containerId ?? ''),
            lastFieldsSection: this.#lastFieldsSectionByContainer.get(this.activeContainer?.containerId ?? ''),
        });
    }

    setNavigationAction(action: string | null) {
        if (!isDevMode()) {
            this.#lastNavigationAction = null;
            return;
        }

        this.#lastNavigationAction = action;
    }

    getSectionFromField(field: NavigationFieldConfigurationInterface): NavigationSectionConfigurationInterface | null {
        if (field) {
            if (this.#registeredSections[field.sectionId]) {
                return this.#registeredSections[field.sectionId];
            }
        }

        return null;
    }

    getSectionFromSectionId(sectionId: string): NavigationSectionConfigurationInterface | null {
        if (sectionId?.length > 0) {
            if (this.#registeredSections[sectionId]) {
                return this.#registeredSections[sectionId];
            }
        }

        return null;
    }

    getContainerFromContainerId(containerId: string): NavigationContainerConfigurationInterface | null {
        if (containerId?.length > 0) {
            if (this.#registeredContainers[containerId]) {
                return this.#registeredContainers[containerId];
            }
        }

        return null;
    }

    getContainerFromSectionId(sectionId: string): NavigationContainerConfigurationInterface | null {
        if (this.#registeredSections[sectionId]) {
            const section = this.#registeredSections[sectionId];

            if (this.#registeredContainers[section.containerId]) {
                return this.#registeredContainers[section.containerId];
            }
        }

        return null;
    }

    getFieldsFromSection(section: NavigationSectionConfigurationInterface): NavigationFieldConfigurationInterface[] {
        if (section) {
            return Object.values(this.#registeredFields)
                .filter((field) => field.sectionId === section.sectionId)
                .sort((a, b) => a.navigationFieldOrder - b.navigationFieldOrder);
        }

        return [];
    }

    getFieldFromFieldId(fieldId: string): NavigationFieldConfigurationInterface {
        if (fieldId?.length > 0) {
            return Object.values(this.#registeredFields)
                .find((field) => field.fieldId === fieldId);
        }

        return null;
    }

    getSectionsFromContainer(container: NavigationContainerConfigurationInterface): NavigationSectionConfigurationInterface[] {
        if (container) {
            return Object.values(this.#registeredSections)
                .filter((section) => section.containerId === container.containerId)
                .sort((a, b) => a.navigationSectionOrder - b.navigationSectionOrder);
        }

        return [];
    }

    /**
     * Trova la sezione successiva più vicina o, se non esiste, la sezione precedente più vicina.
     * @param clickEvent L'evento di click.
     * @returns Un oggetto contenente la sezione più vicina e la sua posizione (successiva o precedente).
     */
    findClosestSection(clickEvent: MouseEvent): {
        section: NavigationSectionConfigurationInterface | null,
        sectionPosition: ClosestSectionPosition | null;
    } {
        const clickY = clickEvent.clientY;

        let closestNextSection: NavigationSectionConfigurationInterface | null = null;
        let closestPreviousSection: NavigationSectionConfigurationInterface | null = null;
        let smallestNextDistance = Infinity;
        let smallestPreviousDistance = Infinity;

        for (const section of this.sectionsFromActiveContainer) {
            const rect = section.element.nativeElement.getBoundingClientRect();

            // Calcola la distanza verticale dal punto del click al bordo superiore della sezione
            const distance = rect.top - clickY;

            if (distance >= 0 && distance < smallestNextDistance) {
                // Se la sezione è sotto il punto del click ed è la più vicina
                smallestNextDistance = distance;

                closestNextSection = section;
            } else if (distance < 0 && Math.abs(distance) < smallestPreviousDistance) {
                // Se la sezione è sopra il punto del click ed è la più vicina
                smallestPreviousDistance = Math.abs(distance);

                closestPreviousSection = section;
            }
        }

        if (closestNextSection) {
            return {
                section: closestNextSection,
                sectionPosition: ClosestSectionPosition.Next
            };
        } else if (closestPreviousSection) {
            return {
                section: closestPreviousSection,
                sectionPosition: ClosestSectionPosition.Previous
            };
        } else {
            return {
                section: null,
                sectionPosition: null
            };
        }
    }

    /**
     * Trova il campo successivo più vicino o, se non esiste, il campo precedente più vicino.
     * @param clickEvent L'evento di click.
     * @returns Un oggetto contenente il campo più vicino e la sua posizione (successivo o precedente).
     */
    findClosestField(clickEvent: MouseEvent): {
        field: NavigationFieldConfigurationInterface | null,
        fieldPosition: ClosestFieldPosition | null;
    } {
        const clickY = clickEvent.clientY;

        let closestNextField: NavigationFieldConfigurationInterface | null = null;
        let closestPreviousField: NavigationFieldConfigurationInterface | null = null;
        let smallestNextDistance = Infinity;
        let smallestPreviousDistance = Infinity;

        for (const field of this.fieldsFromActiveContainer) {
            const rect = field.element.nativeElement.getBoundingClientRect();

            // Calcola la distanza verticale dal punto del click al bordo superiore del campo
            const distance = rect.top - clickY;

            if (distance >= 0 && distance < smallestNextDistance) {
                // Se il campo è sotto il punto del click ed è il più vicino
                smallestNextDistance = distance;

                closestNextField = field;
            } else if (distance < 0 && Math.abs(distance) < smallestPreviousDistance) {
                // Se il campo è sopra il punto del click ed è il più vicino
                smallestPreviousDistance = Math.abs(distance);

                closestPreviousField = field;
            }
        }

        if (closestNextField) {
            return {
                field: closestNextField,
                fieldPosition: ClosestFieldPosition.Next
            };
        } else if (closestPreviousField) {
            return {
                field: closestPreviousField,
                fieldPosition: ClosestFieldPosition.Previous
            };
        } else {
            return {
                field: null,
                fieldPosition: null
            };
        }
    }
}
